import { Router } from 'express';
import { authMiddleware } from '../../middleware/auth.middleware';
import { requirePermission } from '../../middleware/rbac.middleware';
import { prisma } from '../../shared/prisma';
import { Response, NextFunction, Request } from 'express';
import { AuthRequest } from '../../middleware/auth.middleware';
import { getIo } from '../../realtime/socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';
import multer from 'multer';
import fs from 'fs';
import path from 'path';

const router = Router();
router.use(authMiddleware);

const updateStorage = multer.diskStorage({
  destination: (_req, _file, cb) => {
    const dest = path.join(process.cwd(), 'updates');
    if (!fs.existsSync(dest)) {
      fs.mkdirSync(dest, { recursive: true });
    }
    cb(null, dest);
  },
  filename: (_req, file, cb) => {
    // Keep the original name for latest.yml and .exe
    cb(null, file.originalname);
  }
});

const updateUpload = multer({
  storage: updateStorage,
  limits: { fileSize: 500 * 1024 * 1024 } // 500MB max
});

// Dashboard stats
router.get('/stats', requirePermission('view_admin_overview'), async (_req: Request, res: Response, next: NextFunction) => {
  try {
    const [users, activeUsers, teams, channels, messages] = await Promise.all([
      prisma.user.count(),
      prisma.user.count({ where: { presence: { not: 'offline' } } }),
      prisma.team.count(),
      prisma.channel.count({ where: { isArchived: false } }),
      prisma.message.count({ where: { isDeleted: false, createdAt: { gte: new Date(Date.now() - 24 * 60 * 60 * 1000) } } }),
    ]);
    res.json({ success: true, data: { users, activeUsers, teams, channels, messagesToday: messages } });
  } catch (err) { next(err); }
});

// System Updates Publisher
router.post('/updates/upload', requirePermission('view_admin_overview'), updateUpload.array('files'), (req: Request, res: Response, next: NextFunction) => {
  try {
    const files = req.files as Express.Multer.File[];
    if (!files || files.length === 0) {
      return res.status(400).json({ success: false, message: 'No files uploaded' });
    }
    res.status(200).json({ success: true, message: 'Update published successfully' });
  } catch (err) {
    next(err);
  }
});

// Audit logs
router.get('/audit-logs', requirePermission('view_audit_logs'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const page = Number(req.query.page) || 1;
    const take = 50;
    const logs = await prisma.auditLog.findMany({
      orderBy: { createdAt: 'desc' },
      take,
      skip: (page - 1) * take,
      include: { actor: { select: { id: true, displayName: true, email: true } } },
    });
    res.json({ success: true, data: logs });
  } catch (err) { next(err); }
});

// Chat logs
router.get('/chat-logs', requirePermission('view_chat_logs'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const page = Number(req.query.page) || 1;
    const { startDate, endDate } = req.query;

    // If filtering by date, don't limit. Otherwise, limit to 1000 to prevent loading too much by default
    const take = (startDate || endDate) ? undefined : 1000;

    const where: any = {};
    if (startDate || endDate) {
      where.createdAt = {};
      if (startDate) where.createdAt.gte = new Date(startDate as string);
      if (endDate) {
        const end = new Date(endDate as string);
        end.setHours(23, 59, 59, 999);
        where.createdAt.lte = end;
      }
    }

    const messages = await prisma.message.findMany({
      where,
      orderBy: { createdAt: 'desc' },
      ...(take ? { take, skip: (page - 1) * take } : {}),
      select: {
        id: true,
        content: true,
        createdAt: true,
        isDeleted: true,
        senderId: true,
        channelId: true,
        conversationId: true,
        sender: { select: { id: true, displayName: true } },
        conversation: {
          select: {
            id: true,
            type: true,
            name: true,
            members: { select: { userId: true, user: { select: { id: true, displayName: true } } } }
          }
        },
        channel: { select: { id: true, name: true, team: { select: { name: true } } } },
        attachments: { select: { id: true, filename: true, fileSize: true, mimeType: true } }
      }
    });

    const formattedLogs = messages.map(m => {
      let to = 'Unknown';
      let type = 'Unknown';
      if (m.channelId && m.channel) {
        to = `${m.channel.team?.name || 'Team'} > ${m.channel.name}`;
        type = 'Channel';
      } else if (m.conversationId && m.conversation) {
        if (m.conversation.type === 'direct') {
          const otherMember = m.conversation.members.find(mem => mem.userId !== m.senderId);
          to = otherMember?.user?.displayName || 'Unknown';
          type = 'Direct Message';
        } else {
          to = m.conversation.name || 'Group Chat';
          type = 'Group';
        }
      }
      return {
        id: m.id,
        from: m.sender?.displayName || 'System',
        to,
        type,
        content: m.content,
        isDeleted: m.isDeleted,
        createdAt: m.createdAt,
        attachments: m.attachments,
      };
    });

    res.json({ success: true, data: formattedLogs });
  } catch (err) { next(err); }
});

// User management
router.get('/users', requirePermission('manage_users'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const users = await prisma.user.findMany({
      select: {
        id: true, email: true, username: true, displayName: true, presence: true, isActive: true, createdAt: true,
        companyId: true, departmentId: true, position: true,
        company: { select: { id: true, name: true } },
        department: { select: { id: true, name: true } }
      },
      orderBy: { createdAt: 'desc' },
    });
    res.json({ success: true, data: users });
  } catch (err) { next(err); }
});

router.patch('/users/:id/toggle-active', requirePermission('manage_users'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const user = await prisma.user.findUnique({ where: { id: req.params.id } });
    if (!user) return res.status(404).json({ success: false, message: 'User not found' });
    const updated = await prisma.user.update({ where: { id: req.params.id }, data: { isActive: !user.isActive } });
    getIo()?.emit(SocketEvents.USER_UPDATED, { id: updated.id, isActive: updated.isActive });
    res.json({ success: true, data: { id: updated.id, isActive: updated.isActive } });
  } catch (err) { next(err); }
});

// Role management
router.get('/roles', requirePermission('manage_users'), async (_req: Request, res: Response, next: NextFunction) => {
  try {
    const roles = await prisma.role.findMany({
      where: { name: { not: { startsWith: 'custom_' } } },
      include: { _count: { select: { userRoles: true } } }
    });
    res.json({ success: true, data: roles });
  } catch (err) { next(err); }
});

router.post('/roles/assign', requirePermission('manage_users'), async (req: AuthRequest, res: Response, next: NextFunction) => {
  try {
    const { userId, roleId, scopeId, scopeType } = req.body;
    const userRole = await prisma.userRole.create({ data: { userId, roleId, scopeId, scopeType } });
    getIo()?.emit(SocketEvents.USER_UPDATED, { id: userId });
    res.json({ success: true, data: userRole });
  } catch (err) { next(err); }
});

router.post('/groups', requirePermission('manage_users'), async (req: AuthRequest, res: Response, next: NextFunction) => {
  try {
    const { name, description, type, userIds } = req.body;

    if (!name || !userIds || !Array.isArray(userIds) || userIds.length === 0) {
      return res.status(400).json({ success: false, message: 'Invalid group data' });
    }

    // Ensure the admin is in the group as well if they want to manage it, or just use the userIds
    // We will just use the exact userIds provided by the wizard.

    const conversation = await prisma.conversation.create({
      data: {
        type: 'group',
        name,
        createdBy: req.user!.sub,
        members: {
          create: userIds.map((id: string) => ({ userId: id }))
        }
      },
      include: {
        members: true
      }
    });

    userIds.forEach((id: string) => {
      getIo()?.to(`user_${id}`).emit(SocketEvents.CONVERSATION_CREATED, conversation);
    });

    res.status(201).json({ success: true, data: conversation });
  } catch (err) { next(err); }
});

// Custom Themes API
router.get('/themes', async (_req: Request, res: Response, next: NextFunction) => {
  try {
    const themes = await prisma.theme.findMany({
      orderBy: { createdAt: 'desc' }
    });
    const workspace = await prisma.workspace.findFirst();
    const settings = workspace?.settings && typeof workspace.settings === 'object'
      ? (workspace.settings as any)
      : {};
    res.json({
      success: true,
      data: themes,
      activeThemeId: settings.activeThemeId || null,
      activeLightThemeId: settings.activeLightThemeId || null,
      activeDarkThemeId: settings.activeDarkThemeId || null
    });
  } catch (err) { next(err); }
});

router.post('/themes/active', requirePermission('view_admin_overview'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const { themeId, lightThemeId, darkThemeId, mode } = req.body;
    let workspace = await prisma.workspace.findFirst();

    const currentSettings = workspace?.settings && typeof workspace.settings === 'object'
      ? (workspace.settings as any)
      : {};

    let newSettings = { ...currentSettings };

    if (mode === 'light') {
      newSettings.activeLightThemeId = lightThemeId;
    } else if (mode === 'dark') {
      newSettings.activeDarkThemeId = darkThemeId;
    } else {
      if (themeId !== undefined) newSettings.activeThemeId = themeId;
      if (lightThemeId !== undefined) newSettings.activeLightThemeId = lightThemeId;
      if (darkThemeId !== undefined) newSettings.activeDarkThemeId = darkThemeId;
    }

    if (!workspace) {
      workspace = await prisma.workspace.create({
        data: {
          name: 'Default Workspace',
          slug: 'default',
          settings: newSettings
        }
      });
    } else {
      await prisma.workspace.update({
        where: { id: workspace.id },
        data: { settings: newSettings }
      });
    }

    getIo()?.emit('GLOBAL_THEME_APPLIED', {
      themeId: newSettings.activeThemeId,
      activeLightThemeId: newSettings.activeLightThemeId,
      activeDarkThemeId: newSettings.activeDarkThemeId
    });

    res.json({
      success: true,
      message: 'Global theme updated successfully',
      activeLightThemeId: newSettings.activeLightThemeId,
      activeDarkThemeId: newSettings.activeDarkThemeId
    });
  } catch (err) { next(err); }
});

router.post('/themes', requirePermission('view_admin_overview'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    const theme = req.body;
    const saved = await prisma.theme.upsert({
      where: { id: theme.id },
      update: {
        name: theme.name,
        description: theme.description,
        icon: theme.icon,
        enabled: theme.enabled ?? true,
        startDate: theme.startDate,
        endDate: theme.endDate,
        colors: theme.colors,
        background: theme.background,
        decorations: theme.decorations,
        animations: theme.animations,
        icons: theme.icons,
        badge: theme.badge,
        accent: theme.accent,
        bg: theme.bg,
        text: theme.text,
        previewBg: theme.previewBg,
        gradient: theme.gradient,
        variables: theme.variables
      },
      create: {
        id: theme.id,
        name: theme.name,
        description: theme.description,
        icon: theme.icon,
        enabled: theme.enabled ?? true,
        startDate: theme.startDate,
        endDate: theme.endDate,
        colors: theme.colors,
        background: theme.background,
        decorations: theme.decorations,
        animations: theme.animations,
        icons: theme.icons,
        badge: theme.badge,
        accent: theme.accent,
        bg: theme.bg,
        text: theme.text,
        previewBg: theme.previewBg,
        gradient: theme.gradient,
        variables: theme.variables
      }
    });

    getIo()?.emit('THEME_UPDATED', saved);
    res.json({ success: true, data: saved });
  } catch (err) { next(err); }
});

router.delete('/themes/:id', requirePermission('view_admin_overview'), async (req: Request, res: Response, next: NextFunction) => {
  try {
    await prisma.theme.delete({ where: { id: req.params.id } });
    getIo()?.emit('THEME_DELETED', { id: req.params.id });
    res.json({ success: true, message: 'Theme deleted successfully' });
  } catch (err) { next(err); }
});

export default router;
